<template>
    <div>
        <header
            class="fixed top-0 left-0 w-full transition z-20"
            :class="{
                'xl:-translate-y-11 is-thin': $store.state.globals.isHeaderThin,
                'is-editor': isEditorPage,
            }"
        >
            <div class="bg-grey-100 dark:bg-grey-800 py-3 hidden xl:flex">
                <div class="container">
                    <div class="flex justify-between items-center">
                        <div class="flex items-center gap-x-10">
                            <div v-for="item in headerItems.slice(0, 2)" :key="item.id">
                                <nuxt-link
                                    class="header-link flex items-center text-black dark:text-white text-sm leading-none font-Inter-Bold hover:underline"
                                    :to="localePath(item.to)"
                                    >{{ $t(item.title) }}
                                </nuxt-link>
                            </div>
                        </div>
                        <div class="flex items-center gap-x-10">
                            <div v-if="$auth.loggedIn" class="flex items-center gap-x-10">
                                <div class="cursor-pointer">
                                    <nuxt-link
                                        :to="localePath('user-favourite-products')"
                                        class="header-link flex items-center text-black dark:text-white text-sm leading-none font-Inter-Bold hover:underline header-icon header-icon header-icon-hearth"
                                        >{{ $t('menu.favourites') }}
                                    </nuxt-link>
                                </div>
                                <div class="cursor-pointer">
                                    <nuxt-link
                                        :to="localePath('user')"
                                        class="header-link flex items-center text-black dark:text-white text-sm leading-none font-Inter-Bold hover:underline header-icon header-icon header-icon-user"
                                        >{{
                                            $auth.user.last_name + ' ' + $auth.user.first_name || $t('menu.user')
                                        }}</nuxt-link
                                    >
                                </div>
                                <div class="cursor-pointer" @click="handleLogout">
                                    <div
                                        class="header-link flex items-center text-black dark:text-white text-sm leading-none font-Inter-Bold hover:underline header-icon header-icon header-icon-login"
                                    >
                                        {{ $t('menu.logout') }}
                                    </div>
                                </div>
                            </div>
                            <div v-else class="flex items-center gap-x-10">
                                <div class="cursor-pointer" @click="openLoginModal">
                                    <span
                                        class="header-link flex items-center text-black dark:text-white text-sm leading-none font-Inter-Bold hover:underline header-icon header-icon header-icon-login"
                                        >{{ $t('menu.login') }}</span
                                    >
                                </div>
                                <div class="cursor-pointer">
                                    <span
                                        class="header-link block flex items-center text-black dark:text-white text-base text-sm leading-none font-Inter-Bold hover:underline header-icon header-icon header-icon-registration"
                                        @click="openRegistrationModal"
                                        >{{ $t('menu.registration') }}</span
                                    >
                                </div>
                            </div>
                            <div class="flex items-center gap-x-3">
                                <nuxt-link
                                    v-for="locale in $i18n.locales"
                                    :key="locale.code"
                                    :to="switchLocalePath(locale.code)"
                                    class="header-link text-black dark:text-white text-sm leading-none font-Inter-Bold hover:underline"
                                    :class="{ 'opacity-40': locale.code !== $i18n.locale }"
                                    >{{ locale.name_short }}
                                </nuxt-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="bg-white dark:bg-grey-900 header-inner max-w-[100dvw] lg:max-w-full">
                <div
                    class="container px-4 lg:px-5 relative transition-all duration-300"
                    :class="isEditorPage ? 'sm:py-1' : 'sm:py-1 lg:py-0'"
                >
                    <div class="absolute right-0 md::right-4 top-full w-full sm:w-auto z-[-1]">
                        <Toast class="kp-toast" :auto-z-index="false">
                            <template #message="{ message }">
                                <img
                                    class="w-8 md:w-10 h-8 md:h-10 object-contain mr-5"
                                    :src="require(`~/assets/images/emojis/${message.emoji}`)"
                                    :alt="message.summary"
                                />
                                <div class="flex flex-col text-white">
                                    <span class="block font-Inter-Bold mb-1 text-sm md:text-base">
                                        {{ message.summary }}
                                    </span>
                                    <span class="block text-xs font-Inter-Regular opacity-80 leading-4">
                                        {{ message.detail }}
                                    </span>
                                </div>
                            </template>
                        </Toast>
                    </div>
                    <div class="flex items-center justify-between mr-2 md:mr-0 relative">
                        <div class="flex lg:hidden items-center gap-x-2 lg:gap-x-6">
                            <HamburgerBtn />
                            <i class="ri-search-line text-[1.6rem] text-black dark:text-white scale-90" @click="openSearchDropdown"></i>
                        </div>

                        <div
                            class="items-center gap-x-4 xl:gap-x-0 flex justify-center lg:justify-start w-max lg:w-full relative transition-all duration-300"
                            :class="[
                                isEditorPage ? 'py-1 md:py-3 lg:py-2 min-h-[3.25rem]' : 'py-1 md:py-3 xl:py-5 min-h-[3.25rem]',
                                isWhispersOfWood && 'lg:justify-between pr-8',
                            ]"
                        >
                            <div class="absolute lg:relative" @click="handleLogoEvents()">
                                <nuxt-link class="md:mr-5 xl:mr-8" to="/" aria-label="Kartapolis" title="Kartapolis">
                                    <Logo
                                        class="translate-y-1 w-40 md:w-[11.75rem] min-w-[10rem] md:min-w-[11.75rem] max-w-[10rem] md:max-w-[11.75rem] md:w-auto"
                                    />
                                </nuxt-link>
                            </div>
                            <div
                                class="fixed lg:relative top-12 lg:top-0 bg-white dark:bg-grey-900 lg:bg-transparent h-[calc(100dvh-3rem)] lg:h-full w-screen left-0 px-5 lg:px-0 pb-0 md::pb-28 lg:pb-0 flex flex-col lg:flex-row pt-4 lg:pt-0 border-t border-black border-opacity-10 lg:border-none overflow-y-scroll lg:overflow-visible nicescroll z-[-1] lg:z-1 lg:w-auto lg:translate-x-0"
                                :class="{
                                    'translate-x-0': $store.state.globals.isMobileMenuOpen,
                                    'translate-x-[-100%]': !$store.state.globals.isMobileMenuOpen,
                                    'transition-all': $device.isMobile,
                                }"
                            >
                                <ProductCategoriesDropdown
                                    class="md:mr-8"
                                    :class="$device.isDesktop ? 'mt-6 lg:mt-0' : 'mt-3 md:mt-0'"
                                />
                                <div v-if="$device.isMobile" class="mt-5 xl:mb-0" @click="closeMobileMenu">
                                    <div
                                        class="transition mb-6 text-center rounded-full bg-grey-25 hover:bg-grey-50 dark:bg-grey-800 dark:hover:bg-grey-700 text-black dark:text-white text-lg py-2 px-4 lg:px-3 border border-black border-opacity-30 cursor-pointer flex lg:hidden items-center justify-center w-full"
                                        @click="openSearchDropdown"
                                    >
                                        <span class="font-Inter-Bold mr-3 text-base">{{ $t('menu.search') }}</span>
                                        <i class="ri-search-2-line text-[1.25rem]"></i>
                                    </div>
                                </div>

                                <transition name="fade" mode="in-out">
                                    <div
                                        class="flex items-start lg:items-center flex-col lg:flex-row gap-x-0 lg:gap-x-5 xl:gap-x-8 gap-y-6 lg:gap-y-0 header-main-items"
                                        :class="$device.isDesktop && 'mt-6 lg:mt-0'"
                                    >
                                        <nuxt-link
                                            :to="localePath('products')"
                                            class="flex justify-center relative h-full items-center"
                                        >
                                            <div
                                                class="header-item text-base w-full font-Inter-Bold text-black dark:text-white leading-none hover:underline"
                                                @click="closeMobileMenu"
                                            >
                                                {{ $t('menu.all_products') }}
                                            </div>
                                        </nuxt-link>

                                        <nuxt-link
                                            :to="localePath('editors')"
                                            class="flex justify-center relative h-full items-center"
                                        >
                                            <div
                                                class="header-item text-base w-full font-Inter-Bold text-black dark:text-white leading-none hover:underline"
                                                @click="closeMobileMenu()"
                                            >
                                                {{ $t('menu.editors') }}
                                            </div>
                                        </nuxt-link>
                                        <nuxt-link
                                            v-if="!isWhispersOfWood"
                                            :to="localePath('magazine')"
                                            class="flex justify-center relative h-full items-center"
                                        >
                                            <div
                                                class="header-item text-base w-full font-Inter-Bold text-black dark:text-white leading-none hover:underline"
                                                @click="closeMobileMenu()"
                                            >
                                                {{ $t('menu.magazine') }}
                                            </div>
                                        </nuxt-link>
                                    </div>
                                </transition>
                                <div class="flex justify-between flex-col mt-6 lg:hidden">
                                    <div class="flex gap-y-6 flex-col">
                                        <div
                                            v-for="item in headerItems.slice(0, 4)"
                                            :key="item.id"
                                            @click="closeMobileMenu()"
                                        >
                                            <nuxt-link
                                                class="text-base font-Inter-Bold text-black dark:text-white leading-none hover:underline"
                                                :to="localePath(item.to)"
                                                >{{ $t(item.title) }}
                                            </nuxt-link>
                                        </div>
                                        <div class="flex items-center gap-x-4" @click="closeMobileMenu()">
                                            <nuxt-link
                                                v-for="locale in $i18n.locales"
                                                :key="locale.code"
                                                :to="switchLocalePath(locale.code)"
                                                class="text-base font-Inter-Bold text-black dark:text-white leading-none hover:underline"
                                                :class="{ 'opacity-40': locale.code !== $i18n.locale }"
                                                >{{ locale.name_short }}
                                            </nuxt-link>
                                        </div>
                                    </div>
                                    <div
                                        class="flex gap-y-4 flex-col mt-8 pt-6 border-t border-black border-opacity-10 lg:border-none lg:pt-0 xl:mt-4 items-start xl:items-baseline"
                                    >
                                        <div v-if="$auth.loggedIn" class="flex flex-col gap-y-4 w-full">
                                            <nuxt-link
                                                :to="localePath('user-favourite-products')"
                                                class="header-link-mobile-wrapper py-2 xl:p-0 xl:bg-transparent w-full xl:w-auto flex justify-center rounded-full xl:justify-end"
                                            >
                                                <span
                                                    class="flex header-link-mobile items-center text-black dark:text-white leading-none font-Inter-Bold hover:underline header-icon text-base header-icon-mobile header-icon-mobile-hearth"
                                                    @click="closeMobileMenu"
                                                    >{{ $t('menu.favourites') }}</span
                                                >
                                            </nuxt-link>
                                            <nuxt-link
                                                :to="localePath('user')"
                                                class="header-link-mobile-wrapper py-2 xl:p-0 bg-blue-100 bg-opacity-10 xl:bg-transparent w-full xl:w-auto flex justify-center rounded-full xl:justify-end"
                                            >
                                                <span
                                                    class="flex header-link-mobile items-center text-blue-100 xl:text-black dark:xl:text-white leading-none font-Inter-Bold hover:underline header-icon text-base header-icon-mobile header-icon-mobile-registration"
                                                    @click="closeMobileMenu"
                                                >
                                                    {{
                                                        $auth.user.last_name + ' ' + $auth.user.first_name ||
                                                        $t('menu.user')
                                                    }}</span
                                                >
                                            </nuxt-link>
                                            <div
                                                class="header-link-mobile-wrapper py-2 xl:p-0 bg-blue-100 xl:bg-transparent w-full xl:w-auto flex justify-center rounded-full xl:justify-end min-h-[2.775rem] lg:h-auto"
                                                @click="handleLogout"
                                            >
                                                <span
                                                    class="flex header-link-mobile items-center text-white xl:text-black dark:xl:text-white leading-none font-Inter-Bold hover:underline header-icon text-base header-icon-mobile header-icon-mobile-login"
                                                    >{{ $t('menu.logout') }}</span
                                                >
                                            </div>
                                        </div>
                                        <div v-else class="flex flex-col gap-y-4 w-full">
                                            <div
                                                class="header-link-mobile-wrapper py-2 xl:p-0 bg-blue-100 xl:bg-transparent w-full xl:w-auto flex justify-center rounded-full xl:justify-end min-h-[2.775rem] lg:h-auto"
                                                @click="openRegistrationModal"
                                            >
                                                <span
                                                    class="flex header-link-mobile items-center text-white xl:text-black dark:xl:text-white leading-none font-Inter-Bold hover:underline header-icon text-base header-icon-mobile header-icon-mobile-login"
                                                    >{{ $t('menu.registration') }}</span
                                                >
                                            </div>
                                            <div
                                                class="header-link-mobile-wrapper py-2 xl:p-0 bg-blue-100 bg-opacity-10 xl:bg-transparent w-full xl:w-auto flex justify-center rounded-full xl:justify-end"
                                                @click="openLoginModal"
                                            >
                                                <span
                                                    class="flex header-link-mobile items-center text-blue-100 xl:text-black dark:xl:text-white leading-none font-Inter-Bold hover:underline header-icon text-base header-icon-mobile header-icon-mobile-registration"
                                                    >{{ $t('menu.login') }}</span
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center gap-x-3 lg:gap-x-6">
                            <nuxt-link v-if="$auth.loggedIn" :to="localePath('user')" class="lg:hidden">
                                <i class="ri-user-line text-[1.6rem] text-black dark:text-white"></i>
                            </nuxt-link>
                            <div
                                class="transition text-center rounded-full bg-grey-25 hover:bg-grey-50 dark:bg-grey-800 dark:hover:bg-grey-700 text-black dark:text-white hidden lg:flex justify-center items-center text-lg border border-black border-opacity-10 cursor-pointer"
                                :class="
                                    isEditorPage
                                        ? 'w-10 h-10'
                                        : 'py-3 lg:py-2 px-4 lg:px-3 min-w-[3rem] min-h-[3rem] max-w-[3rem] max-h-[3rem] w-[3rem] h-[3rem]'
                                "
                                @click="openSearchDropdown"
                            >
                                <i class="ri-search-2-line text-[1.5rem]"></i>
                            </div>
                            <CartButton />
                        </div>
                    </div>
                </div>
                <ReadProgressBar />
            </div>
            <LoginModal :is-open="$store.state.globals.isLoginModalOpen" :close="closeLoginModal" />
            <RegistrationModal
                :is-open="$store.state.globals.isRegistrationModalOpen"
                :close="closeRegistrationModal"
            />
            <UserModal :is-open="isUserModalOpen" :close="closeUserModal" />
        </header>
        <SearchDropdown
            :is-open="isSearchDropdownOpen"
            :close="closeSearchDropdown"
            :class="$store.state.globals.isHeaderThin && 'xl:-translate-y-11'"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Toast from 'primevue/toast';
import Logo from '~/components/UI/Logo.vue';
import ProductCategoriesDropdown from '@/components/ProductCategoriesDropdown.vue';
import HamburgerBtn from '@/components/UI/Buttons/HamburgerBtn.vue';
import UserModal from '~/components/Modals/UserModal.vue';
import RegistrationModal from '~/components/Modals/RegistrationModal.vue';
import LoginModal from '~/components/Modals/LoginModal.vue';
import CartButton from '~/components/UI/Buttons/CartButton.vue';
import SearchDropdown from '~/components/Forms/SearchDropdown.vue';
import ReadProgressBar from '~/components/UI/Buttons/ReadProgressBar.vue';
export default {
    name: 'Header',
    components: {
        Logo,
        ProductCategoriesDropdown,
        HamburgerBtn,
        UserModal,
        RegistrationModal,
        LoginModal,
        CartButton,
        SearchDropdown,
        ReadProgressBar,
        Toast,
    },
    data() {
        return {
            headerItems: [
                { id: 3, title: 'menu.contact', to: 'contact', icon: '' },
                { id: 4, title: 'menu.career', to: 'career', icon: '' },
            ],
            lastScroll: 0,
            isSearchDropdownOpen: false,
            isLoginModalOpen: false,
            isRegistrationModalOpen: false,
            isUserModalOpen: false,
            isErrorVisible: false,
            isWhispersOfWood: false,
            currentUserTab: 1,
            shownNotifications: 0,
        };
    },
    computed: {
        ...mapGetters({
            cartItemCount: 'cartItemCount',
        }),
        isEditorPage() {
            const name = this.getRouteBaseName();
            return name === 'editors' || (name && name.startsWith('editors-'));
        },
    },
    watch: {
        '$store.state.globals.notifications'(list) {
            // store keeps every notification, so only show the new ones
            list.slice(this.shownNotifications).forEach(({ title, subtitle, emoji }) =>
                this.$toast.add({ summary: title, detail: subtitle, emoji, life: 6000 })
            );
            this.shownNotifications = list.length;
        },
        '$store.state.globals.isMobileMenuOpen'(isOpen) {
            if (isOpen) this.isSearchDropdownOpen = false;
        },
        isEditorPage() {
            this.$nextTick(this.syncHeaderHeight);
        },
        isLoginModalOpen(value) {
            if (value) {
                this.$store.commit('globals/toggleMobileMenu', false);
            }
        },
        isRegistrationModalOpen(value) {
            if (value) {
                this.$store.commit('globals/toggleMobileMenu', false);
            }
        },
    },
    mounted() {
        this.isWhispersOfWood = this.$route.path === '/whispers-of-wood';
        window.addEventListener('scroll', this.toggleHeader);
        this.headerEl = this.$el.querySelector('header');
        this.headerObserver = new ResizeObserver(this.syncHeaderHeight);
        this.headerObserver.observe(this.headerEl);
        this.syncHeaderHeight();
        // this.$store.dispatch('auth/fetchUser');
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.toggleHeader);
        if (this.headerObserver) this.headerObserver.disconnect();
    },
    methods: {
        syncHeaderHeight() {
            const root = document.getElementById('__layout__content');
            if (!root || !this.headerEl) return;
            if (!this.isEditorPage) {
                root.style.removeProperty('--header-height');
                root.style.removeProperty('--header-offset');
                return;
            }
            const inner = this.headerEl.querySelector('.header-inner');
            // preview size stays on the main (white) bar; the grey top bar only hides on scroll
            const main = (inner || this.headerEl).offsetHeight;
            root.style.setProperty('--header-height', `${this.headerEl.offsetHeight}px`);
            root.style.setProperty('--header-offset', `${main}px`);
        },
        toggleHeader() {
            const scrollTop = window.scrollY;
            if (window.innerWidth > 1100 || this.$device.isDesktop) {
                this.$store.commit('globals/setHeaderThin', scrollTop >= this.lastScroll);
                this.lastScroll = window.scrollY;
            }
        },
        openLoginModal() {
            this.$store.commit('globals/toggleLoginModal', true);
        },
        closeLoginModal() {
            this.$store.commit('globals/toggleLoginModal', false);
        },
        handleLogoEvents() {
            this.$scrollToTop();
            if (this.$device.isMobile) {
                this.$store.commit('globals/toggleMobileMenu', false);
                this.isSearchDropdownOpen = false;
            }
        },
        handleLogout() {
            this.$auth.logout();
            if (this.$device.isDesktop) {
                this.$store.commit('globals/setHeaderThin', true);
                setTimeout(() => {
                    this.$store.commit('globals/setHeaderThin', false);
                }, 750);
            } else {
                this.$store.commit('globals/toggleMobileMenu', false);
            }
            this.$router.push('/');
            setTimeout(() => {
                this.$store.commit('globals/setNotifications', {
                    title: this.$t('layout.logout_toast.title'),
                    subtitle: this.$t('layout.logout_toast.subtitle'),
                    emoji: 'waving_hand_emoji.png',
                });
            }, 2000);
        },
        closeMobileMenu() {
            if (window.innerWidth > 1023) return;
            this.$store.commit('globals/toggleMobileMenu', false);
            if (this.$store.state.globals.isMobileMenuOpen) {
                this.isSearchDropdownOpen = false;
            }
        },
        openRegistrationModal() {
            this.$store.commit('globals/toggleRegistrationModal', true);
        },
        closeRegistrationModal() {
            this.$store.commit('globals/toggleRegistrationModal', false);
        },
        closeUserModal() {
            this.isUserModalOpen = false;
        },
        openSearchDropdown() {
            this.$store.commit('globals/toggleMobileMenu', false);
            this.isSearchDropdownOpen = true;
        },
        closeSearchDropdown() {
            this.isSearchDropdownOpen = false;
        },
    },
};
</script>

<style scoped>
header {
    box-shadow: 0 4px 20px rgba(29, 29, 29, 0.03);
}

.header-inner {
    box-shadow: 0 4px 21px rgba(29, 29, 29, 0.1);
}

.search-btn {
    background-image: url('~/assets/images/icons/magnifier-icon.svg');
    @media (max-width: 992px) {
        background-size: 1.6rem;
    }
}

.cart-btn {
    background-image: url('~/assets/images/icons/cart-icon.svg');
    @media (max-width: 1200px) {
        background-size: 1.4rem;
        outline: 1px solid #08dc6a !important;
    }
}

html.dark .header-icon::before,
html.dark .header-icon-mobile:not(.header-icon-mobile-login)::before {
    filter: invert(1);
}

.header-link {
    &.header-icon {
        &::before {
            content: '';
            background-size: auto 1rem;
            @apply h-5 w-5 bg-no-repeat bg-center bg-contain block mr-3 transition-all;
        }
    }

    &.header-icon-registration {
        &::before {
            background-image: url('~/assets/images/icons/registration-icon.svg');
        }
    }

    &.header-icon-hearth {
        &::before {
            background-image: url('~/assets/images/icons/hearth-icon-black.svg');
        }
    }

    &.header-icon-login {
        &::before {
            background-image: url('~/assets/images/icons/login-icon.svg');
        }
    }

    &.header-icon-user {
        &::before {
            background-size: 1.45rem;
            background-image: url('~/assets/images/icons/user-icon.svg');
        }
    }
}

.header-link-mobile {
    &.header-icon-mobile {
        &::before {
            content: '';
            background-size: auto 1rem;
            @apply h-5 w-5 bg-no-repeat bg-center bg-contain block mr-3 transition-all;
        }
    }

    &.header-icon-mobile-registration {
        &::before {
            background-image: url('~/assets/images/icons/registration-icon.svg');
        }
    }

    &.header-icon-mobile-login {
        &::before {
            background-image: url('~/assets/images/icons/login-icon-white.svg');
        }
    }

    &.header-icon-mobile-hearth {
        &::before {
            background-image: url('~/assets/images/icons/hearth-icon-black.svg');
        }
    }

    &.header-icon-mobile-search {
        &::before {
            background-image: url('~/assets/images/icons/magnifier-icon.svg');
        }
    }

    &.header-icon-mobile-user {
        &::before {
            background-size: 1.45rem;
            background-image: url('~/assets/images/icons/user-icon.svg');
        }
    }
}

.header-link-mobile-wrapper {
    &:nth-of-type(2) {
        @apply bg-transparent;

        .header-link-mobile {
            @apply text-black dark:text-white;
        }
    }
}

.is-search-field-open {
    background-image: url('~/assets/images/icons/close-icon-black.svg');
    background-size: 1.29rem;
}
</style>

<style>
/* PrimeVue Toast skinned to match the previous custom ToastMessage (selectors outrank the theme in _prime-vue.pcss) */
.p-toast.kp-toast {
    position: static;
    width: auto;
    opacity: 1;

    /* overlapping stack: every toast shares one grid cell, newest (last child) in front; hover fans them out */
    & > div {
        display: grid;
    }

    .p-toast-message {
        @apply flex items-center p-4 md:p-5 m-0 rounded-none sm:rounded-xl bg-black bg-opacity-90 border-0 shadow-none md:min-w-[24rem] max-w-full md:max-w-[24rem];

        -webkit-backdrop-filter: blur(12px);
        backdrop-filter: blur(12px);

        --i: 0;

        grid-area: 1 / 1;
        transform-origin: bottom center;
        transform: translateY(calc(var(--i) * 0.75rem)) scale(calc(1 - var(--i) * 0.05));
        transition: transform 300ms, opacity 300ms;

        .p-toast-message-content {
            @apply relative flex items-center w-full p-0 border-0;
            transition: opacity 300ms;
        }

        .p-toast-icon-close {
            @apply absolute w-4 md:w-5 h-4 md:h-5 flex items-center justify-center rounded-full right-0 md:-right-2 top-0 md:-top-2 bg-white text-black scale-100 hover:scale-110 hover:bg-white transition-all cursor-pointer;
        }

        .p-toast-icon-close-icon {
            @apply text-[0.5rem] md:text-[0.6rem] font-bold;
        }

        &:nth-last-child(2) {
            --i: 1;
        }

        &:nth-last-child(3) {
            --i: 2;
        }

        &:nth-last-child(4) {
            --i: 3;
        }

        &:nth-last-child(5) {
            --i: 4;
        }

        /* shortcut: only the 5 newest are ever shown, raise the --i list if more must fan out */
        &:nth-last-child(n + 6) {
            opacity: 0;
            pointer-events: none;
        }

        &:nth-last-child(n + 2) .p-toast-message-content {
            opacity: 0;
        }

        &:nth-last-child(n + 4) {
            opacity: 0;
        }
    }

    &:hover .p-toast-message {
        /* % is the toast's own height, toasts share one template so heights match */
        transform: translateY(calc(var(--i) * (100% + 1rem)));

        &:nth-last-child(n + 4):not(:nth-last-child(n + 6)) {
            opacity: 1;
        }

        .p-toast-message-content {
            opacity: 1;
        }
    }

    .p-toast-message-enter-active,
    .p-toast-message-leave-active {
        transition: all 250ms;
    }

    .p-toast-message-enter,
    .p-toast-message-leave-to {
        opacity: 0.01;
        transform: translateX(1rem);

        @media (max-width: 767px) {
            transform: translateY(-1rem);
        }
    }
}
</style>
